import { useQuery } from "@apollo/client";
import type { FacilityHit, PersonHit } from "@/components/AlgoliaSearch";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { navigableGlobalSearchHits, toGlobalSearchFacilityHit, toGlobalSearchPersonHit } from "./mapGlobalSearchHits";
import {
  GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
  GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY,
  GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY
} from "./queries";

const EMPTY_PEOPLE: PersonHit[] = [];
const EMPTY_FACILITIES: FacilityHit[] = [];
const EMPTY_RECENTLY_VIEWED_IDS: GlobalSearchRecentlyViewedIds = { people: [], facilities: [] };

export type GlobalSearchRecentlyViewedIds = {
  people: readonly string[];
  facilities: readonly string[];
};

export type GlobalSearchEntitySearch = {
  peopleHits: PersonHit[];
  facilityHits: FacilityHit[];
  pending: boolean;
  failed: boolean;
  peopleFailed: boolean;
  facilitiesFailed: boolean;
};

export function useGlobalSearchEntitySearch(
  query: string,
  enabled: boolean,
  recentlyViewedIds: GlobalSearchRecentlyViewedIds = EMPTY_RECENTLY_VIEWED_IDS
): GlobalSearchEntitySearch {
  const trimmed = query.trim();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });

  const skip = !enabled || !trimmed;
  const skipPeople = skip || !canViewPeople;
  const skipFacilities = skip || !canViewFacilities;
  const peopleQuery = useQuery(GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.people as string[]
    },
    skip: skipPeople,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });
  const facilitiesQuery = useQuery(GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.facilities as string[]
    },
    skip: skipFacilities,
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const queryReady = enabled && Boolean(trimmed);
  const inFlight = (!skipPeople && peopleQuery.loading) || (!skipFacilities && facilitiesQuery.loading);
  const pending = queryReady && inFlight;

  if (!queryReady || inFlight) {
    return {
      peopleHits: EMPTY_PEOPLE,
      facilityHits: EMPTY_FACILITIES,
      pending,
      failed: false,
      peopleFailed: false,
      facilitiesFailed: false
    };
  }

  const peopleFailed = !skipPeople && Boolean(peopleQuery.error);
  const facilitiesFailed = !skipFacilities && Boolean(facilitiesQuery.error);
  const peopleNodes = peopleQuery.data?.fuzzySearchPeople?.nodes;
  const facilityNodes = facilitiesQuery.data?.fuzzySearchFacilities?.nodes;
  const peopleHits =
    skipPeople || peopleFailed
      ? EMPTY_PEOPLE
      : navigableGlobalSearchHits((peopleNodes ?? []).map(toGlobalSearchPersonHit));
  const facilityHits =
    skipFacilities || facilitiesFailed
      ? EMPTY_FACILITIES
      : navigableGlobalSearchHits((facilityNodes ?? []).map(toGlobalSearchFacilityHit));

  return {
    peopleHits,
    facilityHits,
    pending: false,
    failed: (peopleFailed || facilitiesFailed) && peopleHits.length === 0 && facilityHits.length === 0,
    peopleFailed,
    facilitiesFailed
  };
}
